import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { isAddress } from 'viem';
import parameterGroupRegistryABI from '../../contracts/tgetherParameterGroupsRegistry.json';
import * as config from '../../config.tsx';

interface RegisterParameterGroupModalProps {
    show: boolean;
    onHide: () => void;
}



const RegisterParameterGroupModal: React.FC<RegisterParameterGroupModalProps> = ({ show, onHide }) => {
    const [name, setName] = useState('');
    const [contractAddress, setContractAddress] = useState('');
    const [abi, setAbi] = useState('');
    const [description, setDescription] = useState('');
    const [encodedAbi, setEncodedAbi] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const handleEncodeAbi = () => {
        try {
            const encoded = btoa(abi);
            setEncodedAbi(encoded);
        } catch (e) {
            setError('Failed to encode ABI. Please check the format.');
            console.error('Encoding error:', e);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form inputs
        if (!name || !contractAddress || !encodedAbi || !description) {
            setError('All fields are required. Make sure to encode the ABI.');
            return;
        }
        if (!isAddress(contractAddress)) {
            setError('Invalid contract address.');
            return;
        }

        setError('');
        setLoading(true);
        setSuccess('');

        try {
            // Write to the smart contract
            const tx = await writeContract(config.config, {
                address: process.env.REACT_APP_PARAMETER_GROUP_REGISTRY_ADDRESS as `0x${string}`,
                abi: parameterGroupRegistryABI.abi,
                functionName: 'registerParameterGroup',
                args: [name, contractAddress, encodedAbi, description],
            });

            // Wait for the transaction receipt
            await waitForTransactionReceipt(config.config, {
                hash: tx,
            });

            setSuccess(`Parameter group "${name}" registered successfully.`);
            setName('');
            setContractAddress('');
            setAbi('');
            setEncodedAbi('');
            setDescription('');
        } catch (err) {
            setError('Failed to register parameter group. Please try again later.');
            console.error('Error registering parameter group:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Register New Parameter Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Parameter Group Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter parameter group name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Contract Address</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter contract address"
                            value={contractAddress}
                            onChange={(e) => setContractAddress(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>ABI</Form.Label>
                        <Form.Control
                            as="textarea"
                            placeholder="Paste ABI here"
                            value={abi}
                            onChange={(e) => setAbi(e.target.value)}
                            rows={4}
                            required
                        />
                        <Button
                            variant="secondary"
                            onClick={handleEncodeAbi}
                            className="mt-2"
                        >
                            Encode ABI
                        </Button>
                        <Form.Control
                            type="text"
                            placeholder="Encoded ABI will appear here"
                            value={encodedAbi}
                            readOnly
                            className="mt-2"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            placeholder="Enter a description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            required
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                        {loading ? 'Registering...' : 'Register Parameter Group'}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default RegisterParameterGroupModal;
